import { Model } from '@vuex-orm/core';
import MontageListAPI from '@/modules/Logistics/api/controllers/MontageListAPI';
import MontageListItem from '@/modules/Logistics/api/models/MontageListItem';
import { MontageCreatePayload, MontageMeta } from '@/modules/Logistics/interfaces/LogisticsInterfaces';

/**
 * MontageList model representing a montage list with items and metadata
 */
export default class MontageList extends Model {
    public id!: string;
    public date!: string;
    public vehicle!: string;
    public installer!: string;
    public otherInstallers!: string;
    public preparedBy!: string;
    public isPrinted!: boolean;
    public createdAt!: string;
    public updatedAt!: string;
    public items!: MontageListItem[];

    public static entity = 'montage_lists';

    public static fields() {
        return {
            id: this.attr(null),
            date: this.attr(null),
            vehicle: this.attr(null),
            installer: this.attr(null),
            otherInstallers: this.attr(null),
            preparedBy: this.attr(null),
            isPrinted: this.boolean(false),
            createdAt: this.attr(null),
            updatedAt: this.attr(null),
            items: this.hasMany(MontageListItem, 'montage_list_id'),
        };
    }

    public static async fetchAll(): Promise<MontageList[]> {
        try {
            const response = await MontageListAPI.getAll();
            const lists = response?.data || [];
            await MontageList.insertOrUpdate({ data: lists });
            return lists;
        } catch (e) {
            return Promise.reject(e);
        }
    }

    public static async fetchById(id: string): Promise<MontageList | null> {
        try {
            const response = await MontageListAPI.getById(id);
            const list = response?.data;
            if (list) {
                await MontageList.insertOrUpdate({ data: list, insertOrUpdate: ['montageListItem'] });
            }
            return list;
        } catch (e) {
            return Promise.reject(e);
        }
    }

    public static async createNew(payload: MontageCreatePayload): Promise<MontageList> {
        try {
            const response = await MontageListAPI.create(payload);
            const list = response?.data;
            if (list) {
                await MontageList.insertOrUpdate({ data: list, insertOrUpdate: ['montageListItem'] });
            }
            return list;
        } catch (e) {
            return Promise.reject(e);
        }
    }

    public static async updateExisting(id: string, payload: Partial<MontageMeta>): Promise<MontageList> {
        try {
            const response = await MontageListAPI.update(id, payload);
            const list = response?.data;
            if (list) {
                await MontageList.insertOrUpdate({ data: list, insertOrUpdate: ['montageListItem'] });
            }
            return list;
        } catch (e) {
            return Promise.reject(e);
        }
    }

    public static async deleteExisting(id: string): Promise<void> {
        try {
            await MontageListAPI.delete(id);
            await MontageList.delete(id);
        } catch (e) {
            return Promise.reject(e);
        }
    }

    public static async getPDF(id: string): Promise<Blob> {
        try {
            const response = await MontageListAPI.getPdf(id);
            return response.data;
        } catch (e) {
            return Promise.reject(e);
        }
    }
}
